import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import type { Recipient } from '@webapp/account/src/resources/mailing/recipient';

import { AlignSetting, getColor } from '@webapp/common/lib/ui';

export enum MailType {
    MAIN = 1,
    RECIP_ADD = 2,
    NOTIFY = 3,
}

export const MAIL_TYPE_LABEL = {
    [MailType.NOTIFY]: 'Напоминание',
    [MailType.MAIN]: 'Основной',
    [MailType.RECIP_ADD]: 'Добавление получателей',
};

export enum MailStatus {
    CREATED = 1,
    STATUS_WAITING_DELIVERY = 2,
    DELETED = 3,
    SENDING = 4,
    SENT = 5,
    CANCELED = 6,
}

export enum RecipientType {
    ALL = 'ALL',
    REPLY = 'REPLY',
    NO_REPLY = 'NO_REPLY',
}

export const MAIL_STATUS_LABEL = {
    [MailStatus.CREATED]: 'Создано',
    [MailStatus.STATUS_WAITING_DELIVERY]: 'Ожидает отправки',
    [MailStatus.DELETED]: 'Удалено',
    [MailStatus.SENDING]: 'Отправляется',
    [MailStatus.SENT]: 'Отправлено',
    [MailStatus.CANCELED]: 'Отменено',
};

export interface MailGroup {
    id: number;
    name: string;
    emails: Array<{
        [key: string]: string;
    }>;
    hidden: boolean;
}

export const initialMailGroup: Readonly<MailGroup> = {
    id: 0,
    name: '',
    emails: [],
    hidden: true,
};

export enum MailEditMode {
    TEMPLATE = 'template',
    HTML = 'html',
}

export interface MailStat {
    all: number;
    answered: number;
    notAnswered: number;
}

export interface MailParams {
    body: string;
    fullHtml: string;
    headerColor: string;
    logoImage: string;
    logoImageName: string; // TODO purge
    logoImageHeight: number;
    logoImageWidth: number;
    logoPos: AlignSetting;
    buttonText: string;
    buttonColor: string;
    editMode: MailEditMode;
    disallowRefilling: boolean;
    excludeDuplicate: boolean;
    excludeDuplicateWho: Array<string>;
    excludeDuplicatePeriod: number;
    expireDate?: dayjs.Dayjs;
    reminderListIds: Array<string>;
}

export interface Mail {
    id: number;
    who: RecipientType;
    mailType: MailType;
    status: MailStatus;

    recipients: Array<Recipient>;
    mailGroups: Array<MailGroup>;
    stat: MailStat; // TODO ?? move to state.stat

    title: string;

    fetch: boolean;

    fetchGroup: boolean;

    template: string;
    params: MailParams;

    bindMail: boolean;

    senderEmail: string;
    senderConfirm: boolean;

    sendNow: boolean;
    sendDatetime: dayjs.Dayjs;

    replyAmount: number;
    emailsAmount: number;

    duplicate: {
        total?: PrimitiveValue;
        ALL_NO_DELETE?: PrimitiveValue;
        REPLY?: PrimitiveValue;
        SEND_NO_REPLY?: PrimitiveValue;
    };
    tags?: Array<string>;
}

export const initialMailParams: Readonly<MailParams> = {
    body: '',
    fullHtml: '',
    headerColor: getColor('white'),
    logoImage: null,
    logoImageName: null,
    logoImageHeight: 0,
    logoImageWidth: 0,
    logoPos: AlignSetting.LEFT,
    buttonText: 'Пройти опрос',
    buttonColor: getColor('blue'),
    editMode: MailEditMode.TEMPLATE,
    disallowRefilling: true,
    excludeDuplicate: false,
    excludeDuplicateWho: ['ALL_NO_DELETE'],
    excludeDuplicatePeriod: 1,
    reminderListIds: [],
};

export const initialMail: Readonly<Mail> = {
    fetch: true,
    fetchGroup: false,
    mailType: MailType.MAIN,
    status: MailStatus.CREATED,
    who: RecipientType.NO_REPLY,
    id: 0,
    recipients: [],
    mailGroups: [],
    stat: {
        all: 0,
        answered: 0,
        notAnswered: 0,
    },
    title: 'Новая рассылка',
    template: '',
    params: cloneDeep(initialMailParams),
    bindMail: true,
    senderEmail: '',
    senderConfirm: false,
    sendNow: true,
    sendDatetime: dayjs(),
    replyAmount: 0,
    emailsAmount: 0,
    duplicate: { ALL_NO_DELETE: 0, REPLY: 0, SEND_NO_REPLY: 0, total: 0 },
};

export enum EmailDetailsMode {
    STAT = 'stat',
    CREATE = 'create',
    VIEW = 'view',
}
